import React, { useEffect, useState } from 'react';
import { 
  CheckboxVisibility, 
  DefaultButton, 
  Dropdown, 
  IconButton, 
  IDropdownOption, 
  Modal, 
  ShimmeredDetailsList, 
  Stack, 
  TextField, 
  TooltipHost
}                                                           from 'office-ui-fabric-react';
import PrimaryButton                                        from 'views/components/UI/PrimaryButton';
import { theme }                                            from "theme";
import styled                                               from 'styled-components';
import { getList, saveElement }                             from 'api/handbooks/table';
import { checkCreate, checkDelete, checkRead, checkUpdate } from 'utils/permissions';
import history                                              from 'utils/history';
import { apiTypes }                                         from 'shared/consts/handbooksApiTypes';

interface CameraTypes {
  id: string;
  name: string;
}

interface IProps {
  dvr_id: number;
  vehicle_id: number;
  cameraTypes: Array<CameraTypes>,
  config: any;
}

interface IItems {
  key: number;
  order_number: number;
  title: string;
  deleteButton: boolean;
  camera_type_id: number;
}


const ModalWrapper = styled.div`
  width: 600px;
  max-height: 90vh;
  overflow-x: hidden;
`;

const HeaderWrapper = styled.div`
  display: flex;
  background-color: ${({theme}) => theme.colors.blue};
  color: ${({theme}) => theme.colors.white};
`;

const DetailsListWrapper = styled.div`
  padding: 10px 30px;
`;

const HeaderTitle = styled.h3`
  flexGrow: 1;
  textAlign: center;
  marginTop: 0;
  marginBottom: 0;
  verticalAlign: center;
`

const ErrorSpan = styled.span`
  width: 55px;
  height: 100%;
  font-size: 9px;
  line-height: 1;
  white-space: normal;
  display: block;
  hyphens: auto;
  text-align: left;
`


const CameraSelection = ({ dvr_id, vehicle_id, config, cameraTypes }: IProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [items, setItems] = useState<(Partial<IItems> | null)[]>([{}]);
  const [oldItems, setOldItems] = useState<(Partial<IItems> | null)[]>([{}]);
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
  const isFullCRUD = checkCreate(history.location.pathname) && checkRead(history.location.pathname) && checkUpdate(history.location.pathname) && checkDelete(history.location.pathname);
  

  useEffect(() => {
    async function getCamerasData() {
      try {
        setIsRequestPending(true);
        const res = await getList({
          table: 'dvrs',
          fields: ['cameras'],
          filters: [{vehicle_id: [vehicle_id?.toString()]}],
          apiType: apiTypes.REPORT_API,
          type: 'table'
        })
        if (res?.list?.[0]?.cameras?.length) {
          setItems([...(res.list[0].cameras.map((cam: any) => ({...cam, key: cam.camera_type_id, deleteButton: true, errorField: null}))), { key: new Date().getTime() }]);
          setOldItems(res?.list?.[0]?.cameras.map(({ dvr_id, camera_type_id, order_number } : any) => ({ dvr_id, camera_type_id, order_number })));
        }
      } catch (error) {
        console.log(error);
      } finally{
        setIsRequestPending(false)
      }
    }
    
    if (isOpen) {
      getCamerasData();
    } else {
      setItems([{}]);
    }
  }, [isOpen]);

  const updateCameras = async () => {
    try {
      setIsRequestPending(true);
      if (!items.filter(item => item?.key && item?.order_number).length) {
        await saveElement({...config, table: 'cameras'}, {
          rows: [{dvr_id}],
          oldRows: oldItems,
        });
      } else {
        await saveElement({...config, table: 'cameras'}, {
          rows: items.filter(item => item?.key && item?.order_number).map(item => ({
            dvr_id: dvr_id,
            camera_type_id: item?.camera_type_id,
            order_number: item?.order_number
          })),
          oldRows: oldItems,
        });
      }
      setIsOpen(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsRequestPending(false);
    }
  }

  const columns = [
    {
      name: 'Номер видеокамеры',
      key: 'order_number',
      fieldName: 'order_number',
      minWidth: 100,
      maxWidth: 100,
      onRender: (props: any) => {
        if (!props.hasOwnProperty('order_number')) {
          return (
            <TooltipHost content={!isFullCRUD ? 'Недостаточно прав доступа' : ''} id={'addCameraButton'} calloutProps={{ gapSpace: 0 }} hidden={isFullCRUD}>
              <div>
                <IconButton
                  onClick={() => 
                    setItems(prev => ([
                      ...prev.filter(item => item && Object.keys(item).filter(key => key !== 'key').length), 
                      {key: new Date().getTime() , order_number: undefined, title: undefined, deleteButton: true, camera_type_id: undefined}, 
                      {}
                    ]))
                  } 
                  iconProps={{iconName: 'Add'}} 
                  style={{
                    backgroundColor: isFullCRUD ? theme.colors.blue : undefined,
                    color: theme.colors.white,
                    margin: '0 auto',
                    display: 'block'
                  }}
                  disabled={!isFullCRUD}
                  aria-labelledby='addCameraButton'
                />
              </div>
            </TooltipHost>
          );
        };
        
        return (
          <TooltipHost content={!isFullCRUD ? 'Недостаточно прав доступа' : ''} id={'cameraOrderNumber'} calloutProps={{ gapSpace: 0 }} hidden={isFullCRUD}>
            <TextField 
              autoComplete='off' 
              onChange={(_, value) => {setItems(prev => {
                return prev.map(item => {
                  if (item?.key === props.key) {
                    return {
                      ...item,
                      order_number: value ? +value : 0,
                    }
                  }
                  return item
                })
              })}} 
              styles={{
                root: {
                  width: 40,
                  margin: '0 auto'
                }, 
                field: {
                  padding: '0 5px'
                }
              }}
              disabled={!isFullCRUD}
              value={props.order_number}
              errorMessage={
                props.error?.includes('order_numberNotUnique') ?
                  <ErrorSpan>Значение должно быть уникальным</ErrorSpan>
                    : props.error?.includes('order_number') ?
                      <ErrorSpan>Обязательное поле</ErrorSpan>
                        : ''}
              aria-labelledby='cameraOrderNumber'
            />
          </TooltipHost>
        )
      }
    },
    {
      name: 'Тип видеокамеры',
      key: 'title',
      fieldName: 'title',
      minWidth: 150,
      maxWidth: 500,
      onRender: (props: any) => {
        if (!props.hasOwnProperty('title')) {
          return <></>
        }

        return (
          <>
            <TooltipHost content={!isFullCRUD ? 'Недостаточно прав доступа' : ''} id={'cameraTitle'} calloutProps={{ gapSpace: 0 }} hidden={isFullCRUD}>
              <Dropdown 
                onChange={
                  (
                    event: React.FormEvent<HTMLDivElement>,
                    item?: IDropdownOption,
                    index?: number | undefined
                  ) => setItems(prev => {
                    return prev.map(pItem => {
                      if (pItem?.key === props.key) {
                        return {
                          ...pItem,
                          camera_type_id: +item?.key!,
                          title: item?.text ?? '',
                          error: null,
                        }
                      }
                      return pItem
                    })
                  })
                }
                disabled={!isFullCRUD}
                options={cameraTypes?.length ? cameraTypes?.map(cam => ({key: +cam.id, text: cam.name})) : props.camera_type_id && props.title ? [{key: props.camera_type_id, text: props.title}] : [{ key: 'nodata', text: 'Нет данных', disabled: true }]}
                defaultSelectedKey={props?.camera_type_id || items?.find(((cam: any) => cam?.camera_type_id === props.camera_type_id))?.camera_type_id}
                errorMessage={props.error?.includes('titleNotUnique') ?
                  'Значение должно быть уникальным'
                    : props.error?.includes('title') ?
                      'Обязательное поле'
                        : ''}
                aria-labelledby='cameraTitle'
              />
            </TooltipHost>
          </>
      )
      }
    },
    {
      name: '',
      key: 'deleteButton',
      fieldName: 'deleteButton',
      minWidth: 40,
      maxWidth: 100,
      onRender: (props: any) => {
        if (!props.hasOwnProperty('deleteButton')) {
          return <></>
        }
        
        return (
          <TooltipHost content={!isFullCRUD ? 'Недостаточно прав доступа' : ''} id={'deleteCameraButton'} calloutProps={{ gapSpace: 0 }} hidden={isFullCRUD} styles={{root: { display: 'inline-block' }}}>
            <IconButton 
              style={{color: theme.colors.themePrimary, backgroundColor: theme.colors.white}} 
              onClick={() => {
                setItems(prev => {
                  return [...prev.filter((item) => item?.key !== props.key)]
                })
              }} 
              iconProps={{ iconName: 'Delete' }}
              disabled={!isFullCRUD}
              aria-labelledby='deleteCameraButton'
            />
          </TooltipHost>
        )
      }
    }
  ]

  const fillInCamerasByDefault = async () => {
    const obj = {
      table: "camera_types",
      fields: ['dvr_id','title','camera_type_id','order_number'],
      sort: {field: "order_number", direction: "asc"},
      limit: 100,
      offset: 0,
      filters: [],
      apiType: apiTypes.REPORT_API,
      type: 'table'
    }

    try {
      setIsRequestPending(true)
      const res = await getList(obj);
    
      setItems([
        ...res.list.map((camera: any) => ({
          key: camera.camera_type_id,
          order_number: camera.order_number,
          title: camera.title,
          deleteButton: true,
          camera_type_id: camera.camera_type_id
        })),
        {}
      ])
    } catch (e) {
      console.log(e);
    } finally {
      setIsRequestPending(false);
    }
  }

  return (
    <>
      {
        dvr_id && 
        <div onClick={() => setIsOpen(true)}>Настроить</div>
      }
      <Modal 
        isOpen={isOpen}
        onDismiss={() => setIsOpen(false)}
        isBlocking={isRequestPending}
      >
        <ModalWrapper>
          <HeaderWrapper>
            <HeaderTitle style={{flexGrow: 1, textAlign: 'center', marginTop: 5, marginBottom: 0, verticalAlign: 'center'}}>Видеокамеры</HeaderTitle>
            <IconButton iconProps={{ iconName: 'ChromeClose' }} style={{flexGrow: 0, color: theme.colors.white}} onClick={() => {setIsOpen(false)}} />
          </HeaderWrapper>
          <DetailsListWrapper>
            <ShimmeredDetailsList
              items={items}
              columns={columns}
              checkboxVisibility={CheckboxVisibility.hidden}
              detailsListStyles={
                {
                  root: {
                    selectors: {
                      '.ms-DetailsHeader': {
                        lineHeight: 16,
                        borderBottom: 'none'
                      },
                      '.ms-DetailsHeader-cell': {
                        whiteSpace: 'normal',
                        height: '100%',
                        textAlign: 'center'
                      },
                      '.ms-DetailsHeader-cell:hover': {
                        backgroundColor: 'transparent'
                      },
                      '.ms-DetailsRow': {
                        borderBottom: 'none',
                        backgroundColor: 'transparent!important'
                      },
                      '.ms-DetailsHeader-cellTitle': {
                        justifyContent: 'space-around',
                        verticalAlign: 'bottom'
                      },
                      '.ms-DetailsRow-cell[data-automation-key="index"]': {
                        display: 'flex',
                        justifyContent: 'space-around'
                      }
                    }
                  }
                }
              }
            />
          </DetailsListWrapper>
          <PrimaryButton 
            disabled={isRequestPending} 
            style={{ marginLeft: 10, backgroundColor: theme.colors.blue }} 
            onClick={fillInCamerasByDefault} 
            >
            Заполнить по умолчанию
          </PrimaryButton>
          <Stack
            horizontal
            tokens={{childrenGap: 10}}
            styles={{root: {padding: '10px;'}}}
            horizontalAlign="space-between"
          >
            <TooltipHost content={!isFullCRUD ? 'Недостаточно прав доступа' : ''} id={'applyButton'} calloutProps={{ gapSpace: 0 }} hidden={isFullCRUD}>
              <PrimaryButton
                style={{ backgroundColor: theme.colors.blue }}
                text="Применить"
                disabled={isRequestPending || !isFullCRUD}
                onClick={() => {
                  const indexMap = new Map();
                  const cameraTypeMap = new Map();

                  const errors = items.map(item => {
                    let error = [];

                    if (item?.order_number && indexMap.has(item?.order_number)) {
                      error.push('order_numberNotUnique');
                    } else {
                      indexMap.set(item?.order_number, true);
                    }

                    if (item?.title && cameraTypeMap.has(item?.title)) {
                      error.push('titleNotUnique')
                    } else {
                      cameraTypeMap.set(item?.title, true);
                    }
                    if (!item?.order_number && item?.title) {
                      error.push('order_number')
                    } else if (item?.order_number && !item.title) {
                      error.push('title')
                    }
                
                    return {
                      ...item,
                      error,
                    };
                  });

                  if (errors.some(cam => !!cam.error.length)) {
                    setItems(errors);
                  } else {
                    updateCameras();
                  }
                }}
                aria-labelledby='applyButton'
              />
            </TooltipHost>
            <DefaultButton
              text="Отмена"
              onClick={() => {setIsOpen(false)}}
              disabled={isRequestPending}
            />
          </Stack>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default CameraSelection;